import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type MouseEvent, type ReactNode } from 'react'
import {
  installOsPatches,
  osPatchesPageQuery,
  osPatchesTableQuery,
  uninstallOsPatches,
  type OsPatchKind,
  type OsPatchManagementRow,
} from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'
import { SoftseeToolbar, SoftseeToolbarEnd } from './SoftseeToolbar'

/**
 * Fila OS Management - opteemum/devices/details/osPatchManagement.php plus
 * cele trei liste (missing / installed / hiddenPatches.php). Filele se
 * deschid pe Missing, ca osPatchManagement-1.js.
 *
 * Install / uninstall pleaca de aici, ca in `PatchesActions` din opteemum.js:
 * butonul "Install patches" / "Uninstall patches" de deasupra tabelului (viu
 * doar cu ceva bifat), "Install" pe un singur rand din Missing, si Install /
 * Uninstall pe randurile din Hidden. Fiecare intreaba intai, apoi posteaza la
 * `/devices/{id}/os-patches/install|uninstall` si redeseneaza lista. Din
 * tichet, comanda poarta si numarul tichetului.
 *
 * Butonul si cautarea stau pe ACELASI rand deasupra tabelului - butonul la
 * stanga, cautarea la dreapta - prin acelasi `SoftseeToolbar` ca Install
 * applications, nu butonul pe un rand si cautarea pe altul.
 */

const TABLE: Record<OsPatchKind, string> = {
  missing: 'deviceMissingPatches',
  installed: 'deviceInstalledPatches',
  hidden: 'deviceHiddenPatches',
}

const TABLE_CLASS: Record<OsPatchKind, string> = {
  missing: 'missing-patches',
  installed: 'installed-patches',
  hidden: 'hidden-patches',
}

type Col = { key: keyof OsPatchManagementRow; title: string; titleHtml?: string; orderable: boolean; width?: number; cls?: string }

const SHARED: Col[] = [
  { key: 'name', title: 'Name', orderable: true, width: 200 },
  { key: 'severity', title: 'Severity', orderable: true },
  { key: 'downloaded', title: 'Downloaded', orderable: true },
  { key: 'size', title: 'Size', orderable: true, width: 100 },
  { key: 'require_source', title: 'Require source', orderable: true, width: 100 },
  { key: 'mandatory', title: 'Mandatory', orderable: true, width: 100 },
  { key: 'beta', title: 'Beta', orderable: true, width: 100 },
  { key: 'reboot', title: 'Reboot behaviour', orderable: true, width: 100 },
  { key: 'action_status', title: 'Action status', orderable: false, width: 100 },
]

const COLUMNS: Record<OsPatchKind, Col[]> = {
  missing: [
    ...SHARED,
    { key: 'action', title: '', orderable: false, width: 80, cls: 'td-small a-center' },
    { key: 'pick', title: '', titleHtml: '<center><input type="checkbox" class="allInputs"/></center>', orderable: false, width: 100 },
  ],
  installed: [
    ...SHARED,
    { key: 'pick', title: '', titleHtml: '<center><input type="checkbox" class="allInputs"/></center>', orderable: false, width: 100 },
  ],
  hidden: [...SHARED, { key: 'actions', title: 'Actions', orderable: false, width: 100 }],
}

export function DeviceOsManagement({ id, ticket }: ScreenProps) {
  const page = useQuery(osPatchesPageQuery(id))
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<OsPatchKind>('missing')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 0, dir: 'asc' })
  const [picked, setPicked] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const table = useQuery({
    ...osPatchesTableQuery(id, {
      kind,
      start,
      length,
      orderColumn: order.column,
      orderDir: order.dir,
      search,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const open = (next: OsPatchKind) => {
    setKind(next)
    setStart(0)
    setSearch('')
    setOrder({ column: 0, dir: 'asc' })
    setPicked([])
  }

  const rows = table.data?.data ?? []
  const pickable = (row: OsPatchManagementRow) => /class="oneInput"/.test(String(row.pick ?? ''))

  /* Trimiterea: aceleasi texte ca swal / toastr din PatchesActions. */
  const send = async (install: boolean, patches: string[], text: string) => {
    if (patches.length === 0 || busy) return
    if (!window.confirm(`Are you sure?\n\n${text}`)) return
    setBusy(true)
    try {
      const result = install ? await installOsPatches(id, patches, ticket ?? 0) : await uninstallOsPatches(id, patches, ticket ?? 0)
      if (!result.ok) {
        toast(result.message ?? `${install ? 'Install' : 'Uninstall'} command could not be sent!`, 'Error', 'error')
        return
      }
      toast(`${install ? 'Install' : 'Uninstall'} command sent.`, 'Success')
      setPicked([])
      await queryClient.invalidateQueries({ queryKey: ['device', id, 'os-patches'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : `${install ? 'Install' : 'Uninstall'} command could not be sent!`, 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  /* Butoanele vin ca HTML din placa (aceleasi celule ca in portal); un
     singur ascultator pe tabel le prinde pe toate. */
  const onTableClick = (ev: MouseEvent<HTMLDivElement>) => {
    const target = ev.target as HTMLElement
    const one = target.closest<HTMLElement>('.install-one-os-update, .install-patch, .uninstall-patch')
    if (!one) return
    ev.preventDefault()
    const patch = one.dataset.patch ?? ''
    if (!patch) return
    const install = !one.classList.contains('uninstall-patch')
    void send(install, [patch], install ? 'This update will be installed' : 'This update will be uninstalled')
  }

  /* Install / Uninstall patches: la stanga randului cautarii. Hidden n-are. */
  const actions =
    kind === 'hidden' ? null : (
      <div className="dt-buttons btn-group">
        <button
          type="button"
          className={`btn ${kind === 'missing' ? 'btn-primary' : 'btn-danger'} patches-action${picked.length === 0 || busy ? ' disabled' : ''}`}
          disabled={picked.length === 0 || busy}
          onClick={() =>
            void send(
              kind === 'missing',
              picked,
              kind === 'missing' ? 'All checked patches will be installed' : 'All checked patches will be uninstalled',
            )
          }
        >
          {kind === 'missing' ? (
            <>
              <i className="far fa-save" aria-hidden="true"></i> Install patches
            </>
          ) : (
            <>
              <i className="fas fa-times-circle" aria-hidden="true"></i> Uninstall patches
            </>
          )}
        </button>
      </div>
    )

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs line-tabs mb-2 patchList" role="tablist">
          {page.data.tabs.map((tab) => (
            <li key={tab.key} className={kind === tab.key ? 'active' : undefined}>
              <a
                href={`#${tab.key}`}
                onClick={(e) => {
                  e.preventDefault()
                  open(tab.key)
                }}
              >
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content" onClick={onTableClick}>
          <PatchTable
            kind={kind}
            actions={actions}
            picked={picked}
            onPick={(patch, on) => setPicked((was) => (on ? [...was.filter((p) => p !== patch), patch] : was.filter((p) => p !== patch)))}
            onPickAll={(on) => setPicked(on ? rows.filter(pickable).map((row) => row.patchId) : [])}
            rows={rows}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            start={start}
            length={length}
            search={search}
            order={order}
            onStart={setStart}
            onLength={(n) => {
              setLength(n)
              setStart(0)
            }}
            onSearch={(q) => {
              setSearch(q)
              setStart(0)
            }}
            onOrder={(next) => {
              setOrder(next)
              setStart(0)
            }}
          />
        </div>
      </div>
    </div>
  )
}

function PatchTable({
  kind,
  actions,
  picked,
  onPick,
  onPickAll,
  rows,
  total,
  fetching,
  error,
  start,
  length,
  search,
  order,
  onStart,
  onLength,
  onSearch,
  onOrder,
}: {
  kind: OsPatchKind
  /** Install / Uninstall patches - la stanga randului cautarii. */
  actions?: ReactNode
  picked: string[]
  onPick(patch: string, on: boolean): void
  onPickAll(on: boolean): void
  rows: OsPatchManagementRow[]
  total: number
  fetching: boolean
  error: string | null
  start: number
  length: number
  search: string
  order: { column: number; dir: 'asc' | 'desc' }
  onStart(n: number): void
  onLength(n: number): void
  onSearch(q: string): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
}) {
  const id = TABLE[kind]
  const cols = COLUMNS[kind]
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${id}_wrapper`}>
      <SoftseeToolbar>
        {actions}
        <SoftseeToolbarEnd>
          <div className="dataTableF">
            <div className="dataTables_filter" id={`${id}_filter`}>
              <label>
                Search:{' '}
                <input
                  type="search"
                  className="form-control input-sm"
                  aria-controls={id}
                  value={search}
                  onChange={(e) => onSearch(e.target.value)}
                />
              </label>
            </div>
          </div>
        </SoftseeToolbarEnd>
      </SoftseeToolbar>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${id}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className={`${TABLE_CLASS[kind]} table table-striped table-hover dataTable no-footer`} width="100%" id={id} role="grid">
            <thead>
              <tr role="row">
                {cols.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    col.cls ?? 'td-small a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      style={col.width ? { width: col.width } : undefined}
                      aria-label={`${col.title}: activate to sort column ${sorted && order.dir === 'asc' ? 'descending' : 'ascending'}`}
                      aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'asc' ? 'desc' : 'asc' })
                      }}
                    >
                      {col.key === 'pick' ? (
                        <center>
                          <input
                            type="checkbox"
                            className="allInputs"
                            checked={rows.length > 0 && rows.every((row) => !/class="oneInput"/.test(String(row.pick ?? '')) || picked.includes(row.patchId)) && picked.length > 0}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => onPickAll(e.target.checked)}
                          />
                        </center>
                      ) : col.titleHtml ? (
                        <span dangerouslySetInnerHTML={{ __html: col.titleHtml }} />
                      ) : (
                        col.title
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={cols.length} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={cols.length} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {cols.map((col) =>
                      col.key === 'pick' ? (
                        <td key={col.key} className={col.cls ?? 'td-small a-left'}>
                          {/class="oneInput"/.test(String(row.pick ?? '')) && (
                            <center>
                              <input
                                type="checkbox"
                                className="oneInput"
                                value={row.patchId}
                                checked={picked.includes(row.patchId)}
                                onChange={(e) => onPick(row.patchId, e.target.checked)}
                              />
                            </center>
                          )}
                        </td>
                      ) : (
                        <td
                          key={col.key}
                          className={col.cls ?? 'td-small a-left'}
                          dangerouslySetInnerHTML={{ __html: String(row[col.key] ?? '') }}
                        />
                      ),
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              Show{' '}
              <select
                name={`${id}_length`}
                aria-controls={id}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${id}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${id}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
